import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { t } from '@lingui/macro'
import styled, { useTheme } from 'styled-components'

import { FlexColumn, FlexRow } from '@/@bothrs-ui/web/components/layout'
import { theme } from '@/theme-generated'

import type { CareplanActivity } from '@healthblocks-io/core'

export const DocumentItem = ({
  document,
  onClick,
}: {
  document: CareplanActivity<
    'Message',
    {
      attachments?: {
        name?: string
        url?: string
        type?: 'pdf' | 'link'
        documentId?: number
      }[]
      messageType?: 'Document' | 'Brochure' | 'Action'
    }
  >
  onClick?: () => void
}) => {
  const theme = useTheme()

  const getDescription = () => {
    switch (document.meta?.messageType) {
      case 'Document': {
        return t({
          id: 'document.document',
          message: 'Document',
        })
      }

      case 'Brochure': {
        return t({
          id: 'document.brochure',
          message: 'Brochure',
        })
      }

      default: {
        return ''
      }
    }
  }

  return (
    <FlexRow
      onClick={onClick}
      flex={1}
      gap={theme.UI.Spacing.Space4}
      justifyContent='space-between'
      alignItems='center'
      style={{
        width: '100%',
      }}
    >
      {/* Icon */}
      <FlexColumn flex={1}>
        <StMessageIcon>
          <FontAwesomeIcon icon={['fal', 'file']} />
        </StMessageIcon>
      </FlexColumn>

      {/* Display & Description */}
      <FlexColumn
        flex={1}
        gap={theme.UI.Spacing.Space1}
        justifyContent='center'
        alignItems='flex-start'
        flexGrow={1}
        style={{ overflow: 'hidden' }}
      >
        <FlexRow
          style={{
            width: '100%',
            justifyContent: 'space-between',
          }}
          gap={theme.UI.Spacing.Space4}
          alignItems='center'
        >
          <StMessageDisplay>{document.meta?.attachments?.[0]?.name ?? document.display}</StMessageDisplay>

          {document.status === 'scheduled' && <UnreadDot />}
        </FlexRow>
        <FlexRow
          style={{
            width: '100%',
            justifyContent: 'space-between',
          }}
          gap={theme.UI.Spacing.Space4}
        >
          <StDocumentDescription>{getDescription()}</StDocumentDescription>

          <StDocumentDate>
            {document.plannedAt
              ? new Date(document.plannedAt).toLocaleDateString('nl-BE', {
                  month: 'long',
                  day: 'numeric',
                })
              : ''}
          </StDocumentDate>
        </FlexRow>
      </FlexColumn>
    </FlexRow>
  )
}

export const StMessageIcon = styled(FlexColumn)`
  border-radius: 100%;

  height: 44px;
  width: 44px;

  align-items: center;
  justify-content: center;

  border: 1px solid ${theme.Colors.Neutrals[50]};
`

const StMessageDisplay = styled.p`
  font-size: ${theme.Typography.BodySmallBold['font-size']};
  line-height: ${theme.Typography.BodySmallBold['line-height']};
  color: ${theme.Colors.Neutrals.Darkest};

  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  font-weight: 700;

  margin: 0;
  padding: 0;
`

const StDocumentDescription = styled.p`
  font-size: ${theme.Typography.BodySmallRegular['font-size']};
  line-height: ${theme.Typography.BodySmallRegular['line-height']};
  color: ${theme.Colors.Neutrals.Darker};
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;

  margin: 0;
  padding: 0;
`

const StDocumentDate = styled.p`
  font-size: ${theme.Typography.BodySmallRegular['font-size']};
  line-height: ${theme.Typography.BodySmallRegular['line-height']};
  color: ${theme.Colors.Neutrals.Darker};
  margin: 0;
  padding: 0;
`

const UnreadDot = styled.div`
  ${({ theme }) => `
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${theme.Colors.Status.Error};
  `}
`
